<!--
PackPurchaseModal — franqueado-facing pack purchase modal (F7).

Opens via eventBus.emit('modal.pack-purchase.open', { franquia_id: N }).
Delegates ALL network calls to the F5 useCreditPurchase composable — no
httpService usage in this component (no-httpservice-in-vue rule).

KEY INVARIANTS:
  - Balance is NEVER updated optimistically. Only after credit.topup.confirmed
    does the parent refetch (BillingThreeSixtyDetail reloadAll).
  - CTA is DISABLED + shows tooltip when purchase_enabled=false (OD-016).
  - Real external checkout_url → redirect (mirrors SubscribeForm.redirectToCheckout).
  - Fake provider → inline pending badge ('aguardando confirmação').
  - The available endpoint returns ONE ROW per (pack, currency, gateway) triple.
    The same pack.id appears multiple times with different providers.
    This component groups those rows into unique packs with per-pack gateway options.
    The user picks (pack, gateway) — the selected gateway's provider_code is sent.

Testid CONTRACT — integration spec + smoke drive these identifiers:
  pack-purchase-modal          — root wrapper
  pack-purchase-pack-list      — pack list container
  pack-item-{id}               — individual pack row (one per unique pack)
  pack-item-{id}-gateway-{code}— gateway option radio within a pack row
  pack-purchase-pending        — inline pending badge (fake provider)
  pack-purchase-confirmed      — confirmed state badge
  pack-purchase-error          — error state
  pack-purchase-disabled-notice — OD-016 disabled notice
  pack-purchase-loading        — loading skeleton
  pack-purchase-empty          — empty packs state
  pack-purchase-confirm        — confirm/submit button (via save-test-id on modal)
-->
<template>
  <modal
    elem-id="pack_purchase"
    :title="t('Comprar créditos')"
    :show="isVisible"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="t('Confirmar compra')"
    save-test-id="pack-purchase-confirm"
    :save-fn="handleConfirm"
    :save-disabled-fn="() => !selectedPackId || !selectedGateway || isPurchasing || pendingPurchase !== null || purchaseEnabled === false"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div data-testid="pack-purchase-modal">
        <!-- OD-016: purchase_enabled=false → disabled notice -->
        <div
          v-if="purchaseEnabled === false"
          class="pack-purchase-disabled-notice"
          data-testid="pack-purchase-disabled-notice"
        >
          <i class="pi pi-info-circle" />
          <span>{{ t('A compra de créditos não está disponível no momento. Entre em contato com o suporte.') }}</span>
        </div>

        <!-- Loading skeleton -->
        <div
          v-if="isLoading"
          class="pack-loading"
          data-testid="pack-purchase-loading"
        >
          {{ t('Carregando pacotes…') }}
        </div>

        <!-- D6: named empty-catalog reason — the franchise's country has no billing market -->
        <div
          v-else-if="emptyReason === 'pais_sem_mercado'"
          class="pack-purchase-disabled-notice"
          data-testid="pack-purchase-pais-sem-mercado"
        >
          <i class="pi pi-info-circle" />
          <span>{{ t('O país desta franquia ainda não possui mercado de créditos configurado') }}</span>
        </div>

        <!-- Pack list — each entry is a unique pack; each pack shows its gateway options -->
        <div
          v-else-if="groupedPacks.length > 0"
          class="pack-list"
          data-testid="pack-purchase-pack-list"
        >
          <div
            v-for="pack in groupedPacks"
            :key="pack.id"
            class="pack-item"
            :class="{
              'pack-item--selected': selectedPackId === pack.id,
              'pack-item--disabled': purchaseEnabled === false,
            }"
            :data-testid="`pack-item-${pack.id}`"
            @click="purchaseEnabled !== false && selectPack(pack.id)"
          >
            <div class="pack-item-radio">
              <i
                :class="selectedPackId === pack.id ? 'pi pi-check-circle' : 'pi pi-circle'"
                class="pack-radio-icon"
              />
            </div>
            <div class="pack-item-info">
              <span class="pack-item-name">{{ pack.name }}</span>
              <span class="pack-item-credits">{{ formatCredits(pack.credits) }} {{ t('cr') }}</span>
            </div>

            <!-- Gateway options — shown within the selected pack row -->
            <div
              v-if="selectedPackId === pack.id && pack.options.length > 1"
              class="pack-gateway-options"
              @click.stop
            >
              <div
                v-for="opt in pack.options"
                :key="opt.provider_code"
                class="pack-gateway-option"
                :class="{
                  'pack-gateway-option--selected': selectedGateway === opt.provider_code,
                  'pack-gateway-option--disabled': !opt.purchase_enabled,
                }"
                :data-testid="`pack-item-${pack.id}-gateway-${opt.provider_code}`"
                @click.stop="opt.purchase_enabled && (selectedGateway = opt.provider_code)"
              >
                <i
                  :class="selectedGateway === opt.provider_code ? 'pi pi-check-circle' : 'pi pi-circle'"
                  class="pack-gateway-radio-icon"
                />
                <span class="pack-gateway-label">{{ opt.provider_code }}</span>
                <span class="pack-gateway-price">{{ formatPrice(opt.price_cents, opt.currency) }}</span>
              </div>
            </div>

            <!-- Single gateway option — show price inline without a sub-picker -->
            <div
              v-else-if="pack.options.length === 1"
              class="pack-item-price"
            >
              {{ formatPrice(pack.options[0].price_cents, pack.options[0].currency) }}
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-else
          class="pack-empty"
          data-testid="pack-purchase-empty"
        >
          {{ t('Nenhum pacote disponível no momento.') }}
        </div>

        <!-- Inline pending state (fake provider — no redirect) -->
        <div
          v-if="pendingPurchase !== null && pendingPurchase.status === 'pending'"
          class="pack-purchase-pending"
          data-testid="pack-purchase-pending"
        >
          <i class="pi pi-clock pack-pending-icon" />
          <div class="pack-pending-text">
            <span class="pack-pending-badge">{{ t('aguardando confirmação') }}</span>
            <span class="pack-pending-sub">{{ t('O pagamento está sendo processado. Sua recarga será creditada assim que confirmada.') }}</span>
          </div>
        </div>

        <!-- Confirmed state -->
        <div
          v-if="pendingPurchase !== null && pendingPurchase.status === 'confirmed'"
          class="pack-purchase-confirmed"
          data-testid="pack-purchase-confirmed"
        >
          <i class="pi pi-check-circle pack-confirmed-icon" />
          <span>{{ t('Recarga confirmada! Seu saldo foi atualizado.') }}</span>
        </div>

        <!-- Error state -->
        <div
          v-if="purchaseError"
          class="pack-purchase-error"
          data-testid="pack-purchase-error"
        >
          <i class="pi pi-times-circle" />
          <span>{{ t('Erro ao processar compra. Tente novamente.') }}</span>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useCreditPurchase } from '../../../composables/useCreditPurchase'
import { formatCredits } from '../formatCredits'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import { isExternalCheckoutUrl } from '../../../utils/checkoutUrl'

interface ModalOpenPayload {
  franquia_id: number
}

/** One gateway option within a grouped pack. */
interface PackGatewayOption {
  provider_code: string
  currency: string
  price_cents: number
  purchase_enabled: boolean
}

/** A unique pack entry with all its gateway options. */
interface GroupedPack {
  id: number
  name: string
  credits: number
  options: PackGatewayOption[]
}

/** Map currency code → BCP-47 locale for n() formatting (mirrors SubscribeForm). */
const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

const { t, n } = useI18n()
const {
  availablePacks,
  pendingPurchase,
  purchaseEnabled,
  emptyReason,
  listAvailablePacks,
  initiatePurchase,
  pollPurchaseStatus,
  _reset,
} = useCreditPurchase()

const isVisible = ref(false)
const isLoading = ref(false)
const isPurchasing = ref(false)
const purchaseError = ref<string | null>(null)
const selectedPackId = ref<number | null>(null)
const selectedGateway = ref<string | null>(null)
const currentFranquiaId = ref<number | null>(null)

/**
 * Group flat pack rows (one per (pack, currency, gateway) triple) into unique
 * pack entries each carrying their gateway options. The backend may return the
 * same pack.id multiple times with different provider_code + currency.
 */
const groupedPacks = computed((): GroupedPack[] => {
  const map = new Map<number, GroupedPack>()
  for (const row of availablePacks.value) {
    if (!map.has(row.id)) {
      map.set(row.id, { id: row.id, name: row.name, credits: row.credits, options: [] })
    }
    if (row.provider_code && row.currency && row.price_cents != null) {
      map.get(row.id)!.options.push({
        provider_code: row.provider_code,
        currency: row.currency,
        price_cents: row.price_cents,
        purchase_enabled: row.purchase_enabled ?? false,
      })
    }
  }
  return Array.from(map.values())
})

/**
 * Format a price_cents value in the given currency using the correct major-unit
 * factor (CLP has no decimal — factor 1; BRL/USD factor 100).
 */
function formatPrice(price_cents: number, currency: string): string {
  const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR'
  const factor = currencyFactor(locale, currency)
  const majorValue = price_cents / factor
  return n(majorValue, { style: 'currency', currency, currencyDisplay: 'symbol' })
}

/**
 * Select a pack and auto-select its first purchasable gateway option (or the
 * first option when none is marked purchasable — edge case).
 */
function selectPack(packId: number): void {
  selectedPackId.value = packId
  const pack = groupedPacks.value.find((p) => p.id === packId)
  if (!pack) return
  // Auto-select: first purchasable option, fallback to first option.
  const firstPurchasable = pack.options.find((o) => o.purchase_enabled)
  selectedGateway.value = (firstPurchasable ?? pack.options[0])?.provider_code ?? null
}

async function openModal(payload: ModalOpenPayload): Promise<void> {
  log('PackPurchaseModal', 'openModal', 'Abrindo modal de compra de pacote', payload)
  currentFranquiaId.value = payload.franquia_id
  selectedPackId.value = null
  selectedGateway.value = null
  purchaseError.value = null
  isVisible.value = true

  isLoading.value = true
  try {
    // listAvailablePacks also sets purchaseEnabled (OD-016) + emptyReason (D6).
    // D6: franquia_id scopes the catalog to this franchise's market country.
    await listAvailablePacks(payload.franquia_id)
  } catch (err) {
    log('PackPurchaseModal', 'openModal', 'Erro ao carregar pacotes', err)
  } finally {
    isLoading.value = false
  }
}

function closeModal(): void {
  log('PackPurchaseModal', 'closeModal', 'Fechando modal de compra de pacote')
  isVisible.value = false
  selectedPackId.value = null
  selectedGateway.value = null
  purchaseError.value = null
  currentFranquiaId.value = null
  _reset()
  eventBus.emit('modal.pack-purchase.close')
}

async function handleConfirm(): Promise<void> {
  if (!selectedPackId.value || !selectedGateway.value || !currentFranquiaId.value) return
  if (purchaseEnabled.value === false) return

  isPurchasing.value = true
  purchaseError.value = null

  const providerCode = selectedGateway.value

  try {
    const purchase = await initiatePurchase(currentFranquiaId.value, selectedPackId.value, providerCode)

    // Real external checkout URL → redirect immediately (mirrors SubscribeForm).
    if (purchase.checkout_url && isExternalCheckoutUrl(purchase.checkout_url)) {
      redirectToCheckout(purchase.checkout_url)
      return
    }

    // Fake / local provider — stay inline; pendingPurchase ref shows the badge.
    // Start bounded polling; errors surface via purchaseError.
    void pollPurchaseStatus(purchase.purchase_id).catch((err: unknown) => {
      log('PackPurchaseModal', 'handleConfirm', 'Poll failed or timed out', err)
      purchaseError.value = String((err as Error).message ?? err)
    })
  } catch (err) {
    log('PackPurchaseModal', 'handleConfirm', 'Erro ao iniciar compra', err)
    purchaseError.value = String((err as Error).message ?? err)
  } finally {
    isPurchasing.value = false
  }
}

/**
 * redirectToCheckout — thin seam so tests can spy instead of patching window.
 */
function redirectToCheckout(url: string): void {
  log('PackPurchaseModal', 'redirectToCheckout', 'Redirecionando para checkout', { url })
  // New tab, always: the SPA stays alive and the user never needs the
  // browser back button to return from the gateway (2026-08-20).
  window.open(url, '_blank', 'noopener')
}

onMounted(() => {
  eventBus.on('modal.pack-purchase.open', openModal as (data?: unknown) => void)
})

onBeforeUnmount(() => {
  eventBus.off('modal.pack-purchase.open', openModal as (data?: unknown) => void)
})

defineExpose({ redirectToCheckout })
</script>

<style scoped>
.pack-purchase-disabled-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--p-yellow-50, #fefce8);
  border: 1px solid var(--p-yellow-300, #fde047);
  border-radius: 6px;
  margin-bottom: 16px;
  color: var(--p-yellow-800, #854d0e);
  font-size: 0.875rem;
}

.pack-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pack-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid var(--p-surface-200, #e2e8f0);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.pack-item:hover:not(.pack-item--disabled) {
  border-color: var(--p-primary-color, #3b82f6);
  background: var(--p-surface-50, #f8fafc);
}

.pack-item--selected {
  border-color: var(--p-primary-color, #3b82f6);
  background: var(--p-primary-50, #eff6ff);
}

.pack-item--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.pack-item-radio {
  flex-shrink: 0;
}

.pack-radio-icon {
  font-size: 1.1rem;
  color: var(--p-primary-color, #3b82f6);
}

.pack-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pack-item-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--p-text-color, #1e293b);
}

.pack-item-credits {
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #64748b);
}

.pack-item-price {
  font-weight: 700;
  font-size: 1rem;
  color: var(--p-text-color, #1e293b);
  flex-shrink: 0;
}

.pack-empty,
.pack-loading {
  padding: 24px;
  text-align: center;
  color: var(--p-text-muted-color, #64748b);
}

.pack-purchase-pending {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--p-yellow-50, #fefce8);
  border: 1px solid var(--p-yellow-300, #fde047);
  border-radius: 8px;
}

.pack-pending-icon {
  font-size: 1.25rem;
  color: var(--p-yellow-600, #ca8a04);
  flex-shrink: 0;
  margin-top: 2px;
}

.pack-pending-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pack-pending-badge {
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--p-yellow-800, #854d0e);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.pack-pending-sub {
  font-size: 0.8rem;
  color: var(--p-yellow-700, #a16207);
}

.pack-purchase-confirmed {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--p-green-50, #f0fdf4);
  border: 1px solid var(--p-green-300, #86efac);
  border-radius: 8px;
  color: var(--p-green-700, #15803d);
  font-weight: 600;
}

.pack-confirmed-icon {
  font-size: 1.25rem;
  color: var(--p-green-600, #16a34a);
}

.pack-purchase-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--p-red-50, #fef2f2);
  border: 1px solid var(--p-red-300, #fca5a5);
  border-radius: 8px;
  color: var(--p-red-700, #b91c1c);
}

.pack-gateway-options {
  width: 100%;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pack-gateway-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--p-surface-200, #e2e8f0);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  font-size: 0.875rem;
}

.pack-gateway-option:hover:not(.pack-gateway-option--disabled) {
  border-color: var(--p-primary-color, #3b82f6);
  background: var(--p-surface-50, #f8fafc);
}

.pack-gateway-option--selected {
  border-color: var(--p-primary-color, #3b82f6);
  background: var(--p-primary-50, #eff6ff);
}

.pack-gateway-option--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.pack-gateway-radio-icon {
  font-size: 0.95rem;
  color: var(--p-primary-color, #3b82f6);
  flex-shrink: 0;
}

.pack-gateway-label {
  flex: 1;
  font-weight: 500;
  color: var(--p-text-color, #1e293b);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.pack-gateway-price {
  font-weight: 700;
  color: var(--p-text-color, #1e293b);
}
</style>
